/**
 * EAT-E-44-03 (ADR 0189). A scripted hand-off pulls this tab off its page onto a node's page, and the person's own
 * Mark as Complete there answers with no redirect: a server-route launch publishes the later navigations on a channel
 * no tab hears. So the tab itself remembers, in sessionStorage (per tab, the scope `XactRunTabService` uses), the URL
 * it LEFT and the URL it was TAKEN TO, and goes back once its press on that page is answered. A page reached by hand
 * remembers nothing; a tab with no storage remembers nothing.
 */
export const HAND_OFF_RETURN_STORAGE_KEY = 'eat.handOff.return';

interface HandOffReturn {
  from: string;
  to: string;
  /** Set once the tab has been sent back: the record is kept so the NEXT hand-off can still read `from`, but never returned twice. */
  returned?: boolean;
}

/**
 * Remember that an unattended navigation is taking this tab from `from` to `to`. When the previous hand-off's page is
 * where the tab still is — the next human node came up before the tab had left the last one (seen live: a four-second
 * scripted node between two human nodes) — the origin carried forward is the previous record's, not the node page.
 */
export function rememberHandOffReturn(from: string, to: string): void {
  const previous = read();
  const origin = previous && from && samePage(previous.to, from) ? previous.from : from;
  if (!origin || !to || samePage(origin, to)) {
    return;
  }
  try {
    window.sessionStorage.setItem(HAND_OFF_RETURN_STORAGE_KEY, JSON.stringify({from: origin, to} as HandOffReturn));
  } catch {
    // No storage: this tab remembers nothing and stays where the press leaves it, as before.
  }
}

/**
 * The URL to go back to, when `currentUrl` is the page the hand-off took this tab to. A match marks the record returned,
 * so a second press on the same page does not bounce the tab again, and a press on some other page never matches.
 */
export function takeHandOffReturn(currentUrl: string): string | null {
  const record = read();
  if (!record || record.returned || !samePage(record.to, currentUrl)) {
    return null;
  }
  try {
    window.sessionStorage.setItem(HAND_OFF_RETURN_STORAGE_KEY, JSON.stringify({...record, returned: true}));
  } catch {
    // Without storage there was no record to read; unreachable in practice.
  }
  return record.from;
}

export function clearHandOffReturn(): void {
  try {
    window.sessionStorage.removeItem(HAND_OFF_RETURN_STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}

function read(): HandOffReturn | null {
  try {
    const raw = window.sessionStorage.getItem(HAND_OFF_RETURN_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed.from === 'string' && typeof parsed.to === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Path and query, with the leading slash made uniform and a trailing slash and a fragment ignored: the Router's `url`
 * reads `/gmt/scenarios/eat/<id>` while the server's RedirectUrl reads `gmt/scenarios/eat/<id>` (seen live, 2026-10-25).
 */
function samePage(a: string, b: string): boolean {
  return normalize(a) === normalize(b);
}

function normalize(url: string): string {
  const withoutFragment = (url || '').split('#')[0];
  const trimmed = withoutFragment.replace(/\/+(\?|$)/, '$1');
  const rooted = trimmed.startsWith('/') ? trimmed : '/' + trimmed;
  return rooted === '/' ? '/' : rooted;
}
